import { isBrowser } from "./types";

/**
 * Almacenamiento persistente del dispositivo (IndexedDB).
 *
 * Es la única pieza de la capa de dispositivo que **sí lanza**: si no hay dónde
 * guardar un registro de asistencia pendiente, callarlo sería peor que fallar. Quien llama decide
 * cómo degradar.
 *
 * No se usa una librería de terceros a propósito. La superficie que necesitamos son
 * cuatro operaciones sobre dos almacenes, y este archivo es justo el que se sustituye
 * por SQLite al envolver con Capacitor (§3.3) — no tiene sentido cargar una
 * dependencia destinada a desaparecer.
 *
 * Nada sensible vive aquí (§10.6): la cola guarda registros de asistencia y formularios pendientes,
 * nunca credenciales.
 */

const DB_NAME = "orquesti-mobile";

/**
 * Subir esta versión crea almacenes nuevos **sin tocar los existentes** — la cola
 * sobrevive a la actualización de la app, que es requisito explícito de §10.4.
 */
const DB_VERSION = 1;

export const OUTBOX_STORE = "outbox";
export const META_STORE = "meta";

export class DeviceStorageError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = "DeviceStorageError";
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function isStorageAvailable(): boolean {
  return isBrowser() && typeof indexedDB !== "undefined";
}

function openDatabase(): Promise<IDBDatabase> {
  if (!isStorageAvailable()) {
    return Promise.reject(
      new DeviceStorageError(
        "Este navegador no permite guardar datos sin conexión.",
      ),
    );
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        const outbox = db.createObjectStore(OUTBOX_STORE, { keyPath: "id" });
        // Para listar la cola en orden de llegada sin recorrerla entera.
        outbox.createIndex("created_at", "created_at");
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: "key" });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      // Otra pestaña pidió una versión nueva: soltamos la conexión para no
      // bloquearla y dejamos que la próxima llamada reabra.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };

    request.onerror = () =>
      reject(
        new DeviceStorageError(
          "No se pudo abrir el almacenamiento local.",
          request.error,
        ),
      );

    // Ocurre en modo privado de algunos navegadores: ni éxito ni error.
    request.onblocked = () =>
      reject(
        new DeviceStorageError(
          "El almacenamiento local está bloqueado por otra pestaña.",
        ),
      );
  });
}

function getDatabase(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = openDatabase().catch((error) => {
      dbPromise = null;
      throw error;
    });
  }
  return dbPromise;
}

/**
 * Ejecuta el trabajo dentro de una transacción y resuelve **cuando la transacción se
 * confirma**, no cuando la petición individual responde. La diferencia importa: sin
 * esperar el `oncomplete` se puede reportar "guardado" y perder el dato al cerrarse
 * la pestaña.
 */
async function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest | void,
): Promise<T> {
  const db = await getDatabase();

  return new Promise<T>((resolve, reject) => {
    let transaction: IDBTransaction;
    try {
      transaction = db.transaction(storeName, mode);
    } catch (error) {
      reject(new DeviceStorageError("No se pudo iniciar la transacción.", error));
      return;
    }

    let result: T;
    const request = work(transaction.objectStore(storeName));
    if (request) {
      request.onsuccess = () => {
        result = request.result as T;
      };
    }

    transaction.oncomplete = () => resolve(result);
    transaction.onabort = () =>
      reject(
        new DeviceStorageError(
          "La escritura local no se completó.",
          transaction.error,
        ),
      );
    transaction.onerror = () =>
      reject(
        new DeviceStorageError(
          "Error al escribir en el almacenamiento local.",
          transaction.error,
        ),
      );
  });
}

export const putRecord = <T,>(storeName: string, value: T): Promise<void> =>
  withStore<void>(storeName, "readwrite", (store) => store.put(value));

export const getRecord = <T,>(
  storeName: string,
  key: IDBValidKey,
): Promise<T | undefined> =>
  withStore<T | undefined>(storeName, "readonly", (store) => store.get(key));

export const getAllRecords = <T,>(storeName: string): Promise<T[]> =>
  withStore<T[]>(storeName, "readonly", (store) => store.getAll()).then(
    (rows) => rows ?? [],
  );

/** Devuelve los registros ordenados por el índice, no por la llave primaria. */
export const getAllRecordsByIndex = <T,>(
  storeName: string,
  indexName: string,
): Promise<T[]> =>
  withStore<T[]>(storeName, "readonly", (store) =>
    store.index(indexName).getAll(),
  ).then((rows) => rows ?? []);

export const deleteRecord = (
  storeName: string,
  key: IDBValidKey,
): Promise<void> =>
  withStore<void>(storeName, "readwrite", (store) => store.delete(key));

export const countRecords = (storeName: string): Promise<number> =>
  withStore<number>(storeName, "readonly", (store) => store.count());

/**
 * Lectura y escritura en **una sola transacción**. Dos pestañas abiertas —o la
 * pestaña y el service worker— intentando reintentar la misma marca es un escenario
 * real; leer y escribir por separado permitiría que una pisara a la otra.
 *
 * Devolver `null` desde `updater` deja el registro intacto.
 */
export async function updateRecord<T>(
  storeName: string,
  key: IDBValidKey,
  updater: (current: T | undefined) => T | null,
): Promise<T | undefined> {
  const db = await getDatabase();

  return new Promise<T | undefined>((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    const readRequest = store.get(key);

    let updated: T | undefined;

    readRequest.onsuccess = () => {
      const next = updater(readRequest.result as T | undefined);
      if (next === null) return;
      updated = next;
      store.put(next);
    };

    transaction.oncomplete = () => resolve(updated);
    transaction.onabort = () =>
      reject(
        new DeviceStorageError(
          "La actualización local no se completó.",
          transaction.error,
        ),
      );
    transaction.onerror = () =>
      reject(
        new DeviceStorageError(
          "Error al actualizar el almacenamiento local.",
          transaction.error,
        ),
      );
  });
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await getRecord<{ key: string; value: T }>(META_STORE, key);
  return row?.value;
}

export const setMeta = <T,>(key: string, value: T): Promise<void> =>
  putRecord(META_STORE, { key, value });

/**
 * Pide al navegador no vaciar este almacenamiento cuando ande escaso de espacio.
 * Sin esto, un teléfono lleno puede borrar marcas pendientes sin avisar. Se llama al
 * arrancar la app; que el navegador lo conceda o no queda fuera de nuestro control.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!isBrowser() || !navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
